/**
 * A service worker cache-szabályai tiszta függvényként, hogy Jest-tel tesztelhetők
 * legyenek (a service-worker.ts csak ezeket köti be a Workbox útvonalaiba).
 *
 * Alapelv: amit itt egyik szabály sem fog meg, azt a service worker NEM kezeli,
 * a kérés változatlanul a hálózatra megy (Convex, /api/, külső szolgáltatások).
 */

/** Saját képtároló hostok (a www-s változat is ugyanazt a /plants/ mappát szolgálja ki). */
const PLANT_IMAGE_HOSTS = new Set(['kertmagad.hu', 'www.kertmagad.hu']);

/** Útvonal-előtagok, amelyeket a navigációs app-shell visszaesés sem fog meg. */
const NAVIGATION_DENYLIST: RegExp[] = [
  /^\/_/, // belső / speciális útvonalak (CRA alapértelmezés)
  /^\/api(\/|$)/, // legacy REST / képszolgáltatás
  /^\/plants\//, // növényképek (az SPA /plants útvonala perjel nélkül marad!)
  /^\/uploads\//, // régi feltöltések
  /^\/health$/, // health check
  /\/[^/?]+\.[^/]+$/, // fájlkiterjesztéses útvonalak (pl. /logo.png, /robots.txt)
];

/**
 * Soha nem cache-elendő kérések: Convex (WebSocket + HTTP actions), /api/ hívások.
 * Az összes cache-szabály ezzel kezdődik, így véletlenül sem kerülhet ilyen válasz
 * a cache-be.
 */
export function isNeverCached(url: URL): boolean {
  const host = url.hostname.toLowerCase();
  if (host.endsWith('.convex.cloud') || host.endsWith('.convex.site')) return true;
  if (host === 'convex.cloud' || host === 'convex.site') return true;
  if (/^\/api(\/|$)/.test(url.pathname)) return true;
  return false;
}

/** SPA-navigáció, amely offline (és online) az előre cache-elt index.html-t kapja. */
export function isAppShellNavigation(url: URL, origin: string, mode: string): boolean {
  if (mode !== 'navigate') return false;
  if (url.origin !== origin) return false;
  if (isNeverCached(url)) return false;
  return !NAVIGATION_DENYLIST.some((re) => re.test(url.pathname));
}

const isPlantImagePath = (url: URL): boolean =>
  PLANT_IMAGE_HOSTS.has(url.hostname.toLowerCase()) && url.pathname.startsWith('/plants/');

/**
 * Kis növényképek: kártya-WebP (`*_card.webp`, ~30–60 KB) és bélyegképek
 * (`*_th.png`, `*_th_medium.png`). Ezek mennek a nagyobb darabszámú cache-be.
 */
export function isPlantThumbnail(url: URL): boolean {
  if (isNeverCached(url) || !isPlantImagePath(url)) return false;
  return /_card\.webp$/i.test(url.pathname) || /_th(_[a-z]+)?\.(png|webp|jpe?g)$/i.test(url.pathname);
}

/**
 * Nagy (eredeti) növényképek, pl. `top.png` (akár 1–1,5 MB) – külön, kis
 * darabszámú cache-be kerülnek, hogy a tárhely ne nőjön meg.
 */
export function isPlantFullImage(url: URL): boolean {
  if (isNeverCached(url) || !isPlantImagePath(url) || isPlantThumbnail(url)) return false;
  return /\.(png|webp|jpe?g|gif|avif)$/i.test(url.pathname);
}

/**
 * Hash-elt CRA build-fájlok (`/static/js|css|media/...`), amelyek nem kerültek a
 * precache-be (pl. 5 MB fölötti chunkok) – a nevük tartalom-hash, ezért CacheFirst.
 */
export function isHashedStaticAsset(url: URL, origin: string): boolean {
  return url.origin === origin && /^\/static\/(js|css|media)\//.test(url.pathname);
}

/** Google Fonts stíluslap és betűfájlok. */
export function isGoogleFontsStylesheet(url: URL): boolean {
  return url.hostname === 'fonts.googleapis.com';
}

export function isGoogleFontsFile(url: URL): boolean {
  return url.hostname === 'fonts.gstatic.com';
}

/** Cache-nevek és korlátok – a jelentésben és a tesztekben is ezekre hivatkozunk. */
export const RUNTIME_CACHES = {
  plantThumbs: { name: 'kertmagad-plant-thumbs-v1', maxEntries: 600, maxAgeSeconds: 30 * 24 * 60 * 60 },
  plantImages: { name: 'kertmagad-plant-images-v1', maxEntries: 40, maxAgeSeconds: 14 * 24 * 60 * 60 },
  staticAssets: { name: 'kertmagad-static-v1', maxEntries: 60, maxAgeSeconds: 30 * 24 * 60 * 60 },
  fontStyles: { name: 'kertmagad-font-styles-v1', maxEntries: 10, maxAgeSeconds: 365 * 24 * 60 * 60 },
  fontFiles: { name: 'kertmagad-font-files-v1', maxEntries: 30, maxAgeSeconds: 365 * 24 * 60 * 60 },
} as const;

/** Az üzenet, amellyel az oldal a várakozó service workert aktiválásra kéri. */
export const SKIP_WAITING_MESSAGE = { type: 'SKIP_WAITING' } as const;
